import { useTranslations } from "use-intl";
import { BankAccountsStatus } from "@/types/payments";

interface MatchModalViewStateInput {
  hasBankAccounts: BankAccountsStatus;
  loading: boolean;
  loadFailed: boolean;
  hasSuggestions: boolean;
  noneOfTheseMatch: boolean;
}

interface MatchModalViewState {
  /** The search completed with zero candidates, or the consultant clicked "None of these
   * match" on a non-empty result (design spec AC6). Intentionally excludes the loading/
   * loadFailed states — both must remain visually and functionally distinct from a
   * confirmed-empty result (AC7/AC8). */
  showAcknowledgment: boolean;
  /** Case b: candidate transactions found, with the "None of these match" escape hatch. */
  showCandidateTable: boolean;
  /** Design spec AC7 — a distinct "still checking" state; the acknowledgment checkbox must
   * never render before this fetch resolves. */
  showStillChecking: boolean;
  /** Design spec AC8 (and #462 finding 3) — a fetch failure is NOT the same as "no
   * suggestions"; must never silently fall through to the acknowledgment checkbox as if a
   * completed, genuinely empty search had run. */
  showLoadFailed: boolean;
  /** #498 whole-branch review finding I2 — a distinct fifth state: the system doesn't yet
   * know whether accounts are connected. Must be checked BEFORE the `hasBankAccounts ===
   * false` case-a branch, since neither the audited (b/c) nor unaudited (a) path may be
   * reached while this is true. */
  showBankStatusUnknown: boolean;
}

/**
 * Shared across every "mark as X / match to transaction" modal (payables, receivables,
 * receipts, issue #498): derives which of the five mutually-exclusive views to render from
 * each modal's own (differently-named) fetch/selection state.
 */
export function useMatchModalViewState({
  hasBankAccounts,
  loading,
  loadFailed,
  hasSuggestions,
  noneOfTheseMatch,
}: MatchModalViewStateInput): MatchModalViewState {
  return {
    showAcknowledgment: hasBankAccounts === true && !loading && !loadFailed && (!hasSuggestions || noneOfTheseMatch),
    showCandidateTable: hasBankAccounts === true && !loading && !loadFailed && hasSuggestions && !noneOfTheseMatch,
    showStillChecking: hasBankAccounts === true && loading,
    showLoadFailed: hasBankAccounts === true && !loading && loadFailed,
    showBankStatusUnknown: hasBankAccounts === "loading" || hasBankAccounts === "error",
  };
}

/**
 * The dialog title, derived from the same view state above plus each caller's own case-a label
 * ("Mark as Paid Externally" / "Mark as Received" / "Mark as Matched Externally") — shared to
 * avoid the identical 5-way ternary chain being triplicated (and to keep it off a single
 * nested-ternary statement, which `sonarjs/no-nested-conditional` flags).
 */
export function useMatchModalTitle() {
  const t = useTranslations("consultantMatching.titles");
  return (
    viewState: Pick<MatchModalViewState, "showBankStatusUnknown" | "showLoadFailed" | "showCandidateTable" | "showStillChecking">,
    hasBankAccounts: BankAccountsStatus,
    caseALabel: string,
  ): string => {
    if (viewState.showBankStatusUnknown) return t("checkingBank");
    if (hasBankAccounts === false) return caseALabel;
    if (viewState.showLoadFailed) return t("loadFailed");
    if (viewState.showCandidateTable || viewState.showStillChecking) return t("match");
    return t("noMatch");
  };
}
